import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

// material-ui
import { makeStyles } from '@mui/styles';
import { Avatar, Box, ButtonBase, Menu, MenuItem, IconButton, Autocomplete, TextField, CircularProgress } from '@mui/material';

import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';

// project imports
import LogoSection from '../LogoSection';
import { Link as RouterLink } from 'react-router-dom';
import configData from '../../config';
import '../../assets/scss/style.css';
import Logo from '../../ui-component/Logo';

// assets
import { IconMenu2 } from '@tabler/icons-react';
import { useSelector } from 'react-redux';
// style constant
const useStyles = makeStyles((theme) => ({
    grow: {
        flexGrow: 1
    },
    headerAvatar: {
        ...theme.typography.commonAvatar,
        ...theme.typography.mediumAvatar,
        transition: 'all .2s ease-in-out',
    },
    iconButton: {
        backgroundColor: '#216bfd',
        color: 'white',
        '&:hover': {
            background: theme.palette.secondary.dark,
        },
      },
    boxContainer: {
        width: '200px',
        display: 'flex',
        [theme.breakpoints.down('md')]: {
            width: 'auto'
        }
    }, 
    disp : {
        bgcolor:'primary.light'
    },
    root: {
        [theme.breakpoints.down('md')]: {
            display: 'none'
        }
    }
}));

//-----------------------|| MAIN NAVBAR / HEADER ||-----------------------//

const Header = ({ handleLeftDrawerToggle }) => {
    const classes = useStyles();
    const account = useSelector((state) => state.account);
    const { name, role, id, token } = account;

    const navigate = useNavigate();

    const [avatarMenu, setAvatarMenu] = useState(null);
    const [searchValue, setSearchValue] = useState('');
    const [searchOptions, setSearchOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isSearchFocused, setIsSearchFocused] = useState(false);

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            if (searchValue.length >= 1) {
                fetchSearchResults(searchValue);
            } else {
                setSearchOptions([]);
            }
        }, 300);

        return () => clearTimeout(delayDebounce);
    }, [searchValue]);

    const fetchSearchResults = async (value) => {
        try {
            setLoading(true);

            const response = await axios.get(
                `${configData.API_SERVER}global-search/?q=${value}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            setSearchOptions(response.data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleAvatarMenuClick = (event) => {
        setAvatarMenu(event.currentTarget);
    };

    const handleAvatarMenuClose = () => {
        setAvatarMenu(null);
    };

    return (
        <React.Fragment>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', height: 48 }}>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton onClick={handleLeftDrawerToggle} sx={{ color: '#fff', p: 0.5 }}>
                    <IconMenu2 />
                </IconButton>
                {/* <Logo /> */}
            </Box>

            <Box sx={{ flexGrow: 1, mx: 3 }}>
                <Box
                    component="form"
                    role="search"
                    autoComplete="off"
                    onSubmit={(e) => e.preventDefault()}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        background: 'rgba(255,255,255,0.15)',
                        borderRadius: 1.5,
                        px: 1.5,
                        height: 32,
                        width: '60%',
                        marginLeft: "100px"
                    }}
                >
                    <Autocomplete
                        freeSolo
                        options={searchOptions}
                        groupBy={(option) => option.type}
                        getOptionLabel={(option) => option.label || ""}
                        loading={loading}
                        inputValue={searchValue}
                        onInputChange={(event, newInputValue, reason) => {
                            if (isSearchFocused && reason === 'input') {
                                setSearchValue(newInputValue);
                            } else if (reason === 'clear') {
                                setSearchValue('');
                            } else if (reason === 'reset' && isSearchFocused) {
                                setSearchValue(newInputValue);
                            }
                        }}
                        onChange={(event, selectedOption) => {
                            if (selectedOption?.redirect_url) {
                                navigate(selectedOption.redirect_url);
                            }
                        }}
                        sx={{
                            width: '100%',
                            background: '#fff',
                            borderRadius: 2
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                id="global-header-search-input"
                                name="global_header_search_query"
                                placeholder="Search Email, Jobs, Candidates, Clients..."
                                variant="outlined"
                                size="small"
                                onFocus={() => setIsSearchFocused(true)}
                                onBlur={() => setIsSearchFocused(false)}
                                inputProps={{
                                    ...params.inputProps,
                                    autoComplete: "off",
                                    autoCorrect: "off",
                                    spellCheck: "false",
                                    role: "searchbox"
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    type: "search",
                                    startAdornment: (
                                        <>
                                            <SearchIcon sx={{ mr: 1, color: 'gray' }} />
                                            {params.InputProps.startAdornment}
                                        </>
                                    ),
                                    endAdornment: (
                                        <>
                                            {loading ? (
                                                <CircularProgress color="inherit" size={18} />
                                            ) : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    )
                                }}
                            />
                        )}
                    />
                </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {/* <IconButton sx={{ color: '#fff' }}>➕</IconButton>
                <IconButton sx={{ color: '#fff' }}>🎁</IconButton>
                <IconButton sx={{ color: '#fff' }}>❓</IconButton> */}
                <IconButton sx={{ color: '#fff', p: 0.5 }}>🔔</IconButton>

                <Avatar sx={{ bgcolor: '#00c2a8', fontSize: 13, width: 30, height: 30, cursor: 'pointer' }} onClick={(e) => handleAvatarMenuClick(e)}>
                    {name?.[0]}
                </Avatar>
                <Menu
                    anchorEl={avatarMenu}
                    open={Boolean(avatarMenu)}
                    onClose={handleAvatarMenuClose}
                >
                    <MenuItem>
                        {name ? name : "User"}
                    </MenuItem>
                </Menu>
            </Box>
        </Box>
            {/* logo & toggler button */}
            {/* <div className={classes.boxContainer}>
                <Box component="span" sx={{ display: { xs: 'none', md: 'block' }, flexGrow: 1 }}>
                    <LogoSection />
                </Box>
                <ButtonBase sx={{ borderRadius: '1px',  overflow: 'hidden' }}>
                    <Avatar variant="rounded" className={classes.headerAvatar} onClick={handleLeftDrawerToggle} color="inherit">
                    <IconButton className={classes.iconButton} >
                    <IconMenu2 />
                </IconButton>
                        
                    </Avatar>
                </ButtonBase>
            </div>
            <Typography variant="h1" component="div" sx={{ flexGrow: 1, textAlign: 'right', p:2, minWidth: 50, fontFamily: 'cursive', color:'#1a8cff' }}>
                <Logo />
            </Typography> */}
            {/* <div className={classes.root}>
                <label className='form-label'>{name}</label>
                <RouterLink to={'/logout'}>
                    <Button variant="contained" >Logout</Button>
                </RouterLink>
            </div> */}
        </React.Fragment>
    );
};

Header.propTypes = {
    handleLeftDrawerToggle: PropTypes.func
};

export default Header;
